import { Clock, FileText, HardDrive, Hash } from 'lucide-react';
import type { ReactNode } from 'react';

import type { ProcessingMetricsResponse } from '../api';

interface DocumentMetaChipsProps {
  metrics?: ProcessingMetricsResponse | null | undefined;
}

const numberFormat = new Intl.NumberFormat('es-ES');
const decimalFormat = new Intl.NumberFormat('es-ES', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Bytes → human size: KB (no decimals) below 1 MB, MB (2 decimals) above, so we never show "0,00 MB". */
function formatSize(bytes: number): string {
  const KB = 1024;
  const MB = KB * KB;
  if (bytes >= MB) {
    return `${decimalFormat.format(bytes / MB)} MB`;
  }
  return `${Math.round(bytes / KB)} KB`;
}

/** Milliseconds → "850 ms" under a second, "1,15 s" above, matching Mistral's compact latency chip. */
function formatLatency(ms: number): string {
  if (ms < 1000) {
    return `${numberFormat.format(ms)} ms`;
  }
  return `${decimalFormat.format(ms / 1000)} s`;
}

function Chip({ icon, label, title }: { icon: ReactNode; label: string; title?: string }): JSX.Element {
  return (
    <span
      className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-xs text-slate-600"
      {...(title !== undefined ? { title } : {})}
    >
      {icon}
      {label}
    </span>
  );
}

/**
 * Compact, non-sensitive processing metrics shown next to the document header — DocuSense's take on
 * Mistral's metadata chips (pages · size · latency · tokens). Each chip is omitted when its value is
 * null, so legacy documents (no OCR metadata, no usage entry) render nothing. No cost is shown here:
 * COGS is admin-only and lives on the AI-usage dashboard.
 */
export function DocumentMetaChips({ metrics }: DocumentMetaChipsProps): JSX.Element | null {
  if (metrics === null || metrics === undefined) {
    return null;
  }

  const chips: JSX.Element[] = [];

  if (metrics.pageCount !== null) {
    chips.push(
      <Chip
        key="pages"
        icon={<FileText className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />}
        label={`${numberFormat.format(metrics.pageCount)} pág.`}
      />,
    );
  }

  if (metrics.fileSizeBytes !== null) {
    chips.push(
      <Chip
        key="size"
        icon={<HardDrive className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />}
        label={formatSize(metrics.fileSizeBytes)}
      />,
    );
  }

  if (metrics.latencyMs !== null) {
    chips.push(
      <Chip
        key="latency"
        icon={<Clock className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />}
        label={formatLatency(metrics.latencyMs)}
        title="Latencia de la extracción"
      />,
    );
  }

  if (metrics.inputTokens !== null || metrics.outputTokens !== null) {
    const input = metrics.inputTokens ?? 0;
    const output = metrics.outputTokens ?? 0;
    const total = input + output;
    const breakdown =
      metrics.cachedInputTokens !== null && metrics.cachedInputTokens > 0
        ? `${numberFormat.format(input)} entrada (${numberFormat.format(metrics.cachedInputTokens)} en caché) · ${numberFormat.format(output)} salida`
        : `${numberFormat.format(input)} entrada · ${numberFormat.format(output)} salida`;
    chips.push(
      <Chip
        key="tokens"
        icon={<Hash className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />}
        label={`${numberFormat.format(total)} tok`}
        title={breakdown}
      />,
    );
  }

  if (chips.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-label="Métricas de procesamiento">
      {chips}
    </div>
  );
}
